import { useState, type RefObject } from "react";
import { CheckCircle2, Copy, Unlock, Send, ExternalLink } from "lucide-react";
import type { DocSummary } from "../../DocumentsOverviewStrip.js";
import { CollapsibleSection } from "../sectionShared.js";
import { AsyncButton } from "../../ui/async-button.js";
import { Badge, type BadgeProps } from "../../ui/badge.js";
import { Button } from "../../ui/button.js";
import { Input } from "../../ui/input.js";
import { Tooltip } from "../../ui/tooltip.js";
import { OnboardingLinkPanel } from "./OnboardingLinkPanel.js";
import { EmergencyContactVerificationPanel, type VerifiableContact } from "./EmergencyContactVerificationPanel.js";
import { BgvReferencesPanel, type BgvState, type ReviewableReference } from "./BgvReferencesPanel.js";
import { OnboardingChecklist, type ChecklistItem } from "./OnboardingChecklist.js";
import type { LightboxTarget } from "../../DocumentLightbox.js";
import { PolicyAssignmentsPanel } from "./PolicyAssignmentsPanel.js";
import type { PolicySigningStatus } from "../../../lib/policySigning.js";

export { REQUIRED_DOC_TYPES, CHECKLIST_DOC_TITLES } from "./OnboardingChecklist.js";

const onbBtnSecondary: React.CSSProperties = {
  padding: "5px 14px", fontSize: 12, fontWeight: 600,
  border: "1.5px solid var(--color-brand-500)", borderRadius: "var(--radius-md)",
  background: "var(--color-surface-0)", color: "var(--color-brand-700)", cursor: "pointer",
};

// The onboarding record's stage drives what HR can do here (the server
// enforces the same rules): IN_PROGRESS — the employee is still filling the
// wizard; PENDING_HR_REVIEW — submitted, HR verifies and activates;
// COMPLETED — read-only.
const STAGE: Record<string, { label: string; tone: BadgeProps["tone"]; hint: string }> = {
  IN_PROGRESS:       { label: "In progress", tone: "info", hint: "The employee is filling in their onboarding. Verification and activation open once they submit." },
  PENDING_HR_REVIEW: { label: "Awaiting HR review", tone: "warning", hint: "Submitted. Review the documents, verify contacts, references and BGV, then confirm ready for payroll." },
  COMPLETED:         { label: "Completed", tone: "success", hint: "Onboarding is complete. This section is read-only." },
  CANCELLED:         { label: "Cancelled", tone: "error", hint: "Onboarding was cancelled." },
};
const NOT_SUBMITTED = "Available after the employee submits their onboarding";
const COMPLETED_REASON = "Onboarding completed";

export interface OnboardingRecord extends BgvState {
  id: string; status: string;
  compensationLocked: boolean; handbookAckedAt: string | null;
  handbookSignedName: string | null;
  wizardStep: number; submittedForReviewAt: string | null;
  activatedAt: string | null; dataConsentAt: string | null;
  accessLinkSentAt: string | null; accessLinkSentTo: string | null; accessTokenExpiresAt: string | null;
  checklistItems: ChecklistItem[];
  emergencyContacts: VerifiableContact[];
  references: ReviewableReference[];
}

interface Props {
  sectionRef: RefObject<HTMLDivElement>;
  isCreate: boolean;
  isHR: boolean;
  isSrHR: boolean;
  onboardingRecord: OnboardingRecord | null;
  initiateOnboarding: () => Promise<void>;
  initiatingOnboarding: boolean;
  isChecklistItemDone: (item: { title: string; isCompleted: boolean }) => boolean;
  docs: DocSummary[];
  scrollToSection: (sid: string) => void;
  recheckDoc: (docId: string) => Promise<void>;
  recheckingDocId: string | null;
  togglingItemId: string | null;
  toggleChecklistItem: (itemId: string, isCompleted: boolean) => Promise<void>;
  navigate: (path: string) => void;
  employeeRouteId: string | undefined;
  docRefreshKeys: Record<string, number>;
  bumpDocRefresh: (type: string) => void;
  setVerifyTarget: (t: LightboxTarget) => void;
  locking: boolean;
  unlockCompensation: (reason: string) => Promise<void>;
  /** Open compensation revision state: waiting on Finance, or sent back by Finance. */
  compStatus: "PENDING" | "REJECTED" | null;
  /** What the employee still has to sign (drives "Get Signing Link"). */
  policyStatus: PolicySigningStatus | null;
  refreshPolicyStatus: () => void;
  requestHandbookSignature: () => Promise<void>;
  hrAcking: boolean;
  signLink: { url: string; expiresAt: string; emailed: boolean; emailError: string | null; sentTo: string } | null;
  copySignLink: () => Promise<void>;
  linkCopied: boolean;
  openSignPopup: () => void;
  adStatus: { adAccountStatus: string | null; adLastError: string | null } | null;
  adLoading: boolean;
  setAdConfirmOpen: (v: boolean) => void;
  adConfirmOpen: boolean;
  createAdAccount: (confirmExisting?: boolean) => Promise<void>;
  adCreating: boolean;
  adExistsPrompt: { upn: string } | null;
  setAdExistsPrompt: (v: { upn: string } | null) => void;
  adResult: { tempPassword: string; tempPasswordSentTo: string | null } | null;
  payrollChecking: boolean;
  activating: boolean;
  confirmReadyForPayroll: () => Promise<void>;
  payrollResult: { ready: boolean; system: string; missing: string[]; message: string } | null;
  /** What still blocks activation (getOnboardingGateIssues); empty when ready. */
  activationBlockers: string[];
  personalEmail: string;
  refreshOnboarding: () => Promise<unknown>;
}

// Keeps a tooltip working on a disabled control (disabled elements get no pointer events).
function WhyDisabled({ reason, children }: { reason: string | null; children: React.ReactNode }) {
  return <Tooltip content={reason}><span className="inline-flex">{children}</span></Tooltip>;
}

export function OnboardingSection({
  personalEmail, refreshOnboarding,
  sectionRef, isCreate, isHR, isSrHR, onboardingRecord,
  initiateOnboarding, initiatingOnboarding, isChecklistItemDone, docs, scrollToSection,
  recheckDoc, recheckingDocId, togglingItemId, toggleChecklistItem, navigate, employeeRouteId,
  docRefreshKeys, bumpDocRefresh, setVerifyTarget, locking, unlockCompensation, compStatus, policyStatus, refreshPolicyStatus, requestHandbookSignature, hrAcking,
  signLink, copySignLink, linkCopied, openSignPopup, adStatus, adLoading, setAdConfirmOpen, adConfirmOpen,
  createAdAccount, adCreating, adExistsPrompt, setAdExistsPrompt, adResult,
  payrollChecking, activating, confirmReadyForPayroll, payrollResult, activationBlockers,
}: Props) {
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");
  if (isCreate || !isHR) return null;

  if (!onboardingRecord) {
    return (
      <CollapsibleSection id="onboarding" sectionRef={sectionRef} title="Onboarding">
        <p style={{ fontSize: 13, color: "var(--color-surface-700)", marginBottom: 10 }}>
          Onboarding hasn't been initiated for this employee yet. Until it is, they cannot use
          the onboarding wizard to submit their details.
        </p>
        <AsyncButton
          onClick={() => initiateOnboarding()}
          confirm={{
            title: "Initiate onboarding?",
            description: "Creates the onboarding record and its checklist, so the employee can submit their own details through the onboarding wizard.",
            actionText: "Initiate Onboarding",
          }}
          disabled={initiatingOnboarding}
        >
          Initiate Onboarding
        </AsyncButton>
      </CollapsibleSection>
    );
  }

  const stage = STAGE[onboardingRecord.status] ?? { label: onboardingRecord.status.replace(/_/g, " "), tone: "neutral" as const, hint: "" };
  const submitted = onboardingRecord.status === "PENDING_HR_REVIEW";
  const completed = onboardingRecord.status === "COMPLETED";
  // Verification work needs the employee's submitted details.
  const reviewReason = completed ? COMPLETED_REASON : submitted ? null : NOT_SUBMITTED;
  const activateReason = completed ? COMPLETED_REASON
    : !submitted ? NOT_SUBMITTED
    : activationBlockers.length ? `Not ready yet: ${activationBlockers.join("; ")}` : null;

  // HRMS-0635: lock every card after "Send Link" until HR actually sends the
  // link. accessLinkSentAt is populated by mintOnboardingLink; once set, it
  // persists across sessions. Native fieldset[disabled] disables every form
  // control inside, and pointer-events-none catches custom click handlers on
  // non-form elements.
  const linkSent = !!onboardingRecord.accessLinkSentAt;

  async function copyPassword() {
    if (!adResult) return;
    await navigator.clipboard.writeText(adResult.tempPassword).then(() => setPasswordCopied(true)).catch(() => {});
  }

  return (
    <CollapsibleSection id="onboarding" sectionRef={sectionRef} title="Onboarding">

      {/* Stage */}
      <div className="border-b border-surface-100 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-[1_1_180px] text-[13px] font-medium">Status</span>
          <Badge tone={stage.tone}>{stage.label}</Badge>
          {onboardingRecord.submittedForReviewAt && (
            <span className="text-xs text-surface-500">Submitted {new Date(onboardingRecord.submittedForReviewAt).toLocaleDateString("en-IN")}</span>
          )}
        </div>
        {stage.hint && <p className="mt-1 text-xs text-surface-500">{stage.hint}</p>}
      </div>

      {employeeRouteId && (
        <OnboardingLinkPanel
          employeeId={employeeRouteId}
          personalEmail={personalEmail}
          record={onboardingRecord}
          onSent={refreshOnboarding}
        />
      )}

      {/* HRMS-0635: everything after "Send Link" is dim and inert until the
          employee has their link. Cards stay visible so HR understands the
          flow at a glance; the native fieldset disable + pointer-events-none
          make them impossible to interact with until linkSent flips true. */}
      <fieldset
        disabled={!linkSent}
        className={!linkSent ? "opacity-60 pointer-events-none" : undefined}
        style={{ border: "none", padding: 0, margin: 0 }}
        aria-describedby={!linkSent ? "onboarding-locked-note" : undefined}
      >
      {!linkSent && (
        <p
          id="onboarding-locked-note"
          className="my-3 rounded-md border border-info-border bg-info-bg-subtle px-3 py-2 text-xs text-info-fg"
        >
          Send the onboarding link to the employee first — the rest of the cards unlock once it is on its way.
        </p>
      )}

      {onboardingRecord.checklistItems.length > 0 && (
        <OnboardingChecklist
          items={onboardingRecord.checklistItems}
          isChecklistItemDone={isChecklistItemDone}
          docs={docs}
          scrollToSection={scrollToSection}
          recheckDoc={recheckDoc}
          recheckingDocId={recheckingDocId}
          togglingItemId={togglingItemId}
          toggleChecklistItem={toggleChecklistItem}
          navigate={navigate}
          employeeRouteId={employeeRouteId}
          readOnly={completed}
        />
      )}

      {/* Process & Compliance */}
      <div>
        <EmergencyContactVerificationPanel
          contacts={onboardingRecord.emergencyContacts}
          canRecord={!reviewReason}
          disabledReason={reviewReason ?? undefined}
          onChanged={refreshOnboarding}
        />

        {employeeRouteId && (
          <BgvReferencesPanel
            employeeId={employeeRouteId}
            bgv={onboardingRecord}
            references={onboardingRecord.references}
            hasBgvReport={docs.some(d => d.type === "BGV_REPORT")}
            canEdit={!reviewReason}
            disabledReason={reviewReason ?? undefined}
            docRefreshKey={docRefreshKeys["BGV_REPORT"] ?? 0}
            bumpDocRefresh={bumpDocRefresh}
            setVerifyTarget={setVerifyTarget}
            onChanged={refreshOnboarding}
          />
        )}

        {/* Compensation Lock — set only by Finance approving a revision; HR can unlock with a logged reason. */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-surface-100 py-2.5">
          <div className="flex min-w-0 flex-[1_1_220px] flex-wrap items-center gap-2">
            <span className="text-[13px] font-medium">Compensation Lock</span>
            {onboardingRecord.compensationLocked
              ? <Badge tone="success" className="gap-1">Locked — approved by Finance <CheckCircle2 size={12} /></Badge>
              : compStatus === "PENDING"
                ? <Badge tone="warning">Awaiting Finance approval</Badge>
                : compStatus === "REJECTED"
                  ? <Badge tone="error">Sent back by Finance — revise in Compensation</Badge>
                  : <span className="text-xs text-surface-500">Not locked — submit a compensation revision for Finance approval</span>}
          </div>
          {isHR && onboardingRecord.compensationLocked && !completed && (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                id="comp-unlock-reason"
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                placeholder="Reason for unlocking (required)"
                maxLength={500}
                className="h-8 w-64"
              />
              <AsyncButton
                onClick={() => unlockCompensation(unlockReason.trim()).then(() => setUnlockReason(""))}
                confirm={{
                  title: "Unlock compensation?",
                  description: "Opens compensation for a new revision. The approved CTC stays in the history, and this unlock is logged with your reason. Finance must approve the new revision to lock it again.",
                  actionText: "Unlock Compensation",
                  tone: "warning",
                }}
                disabled={locking || unlockReason.trim().length < 3}
                variant="outline"
                animated={false}
                size="sm"
              >
                <Unlock size={12} /> Unlock Compensation
              </AsyncButton>
            </div>
          )}
        </div>

        {/* Policy documents — HR ticks which ones this employee must sign; only
            the employee can ever sign (the old "ack on their behalf" bypass was
            removed deliberately). HR's other role is to hand over a signing
            link: emailed when SES is configured, and always shown below for
            manual sharing, which is the working path while email is off. */}
        {employeeRouteId && (
          <PolicyAssignmentsPanel employeeId={employeeRouteId} readOnly={completed} onChanged={refreshPolicyStatus} />
        )}
        {/* No `confirm` prop on this one: the handler opens the signing popup
            synchronously to stay inside this click's user activation, and awaiting a
            confirmation card first would lose it to the popup blocker. Generating a
            link is reversible anyway. */}
        {isHR && policyStatus?.required && !completed && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-surface-100 py-2.5">
            <span className="text-xs text-surface-500">Waiting for the employee to sign: {policyStatus.outstanding.map(d => d.title).join(", ")}</span>
            <AsyncButton onClick={() => requestHandbookSignature()} disabled={hrAcking} variant="outline" animated={false} size="sm"
              title="Generates a secure link for the employee to read and sign the assigned documents themselves. Emailed too, when email is configured.">
              <Send size={12} /> Get Signing Link
            </AsyncButton>
          </div>
        )}

        {signLink && (
          <div style={{
            margin: "0 0 10px", padding: "10px 12px", borderRadius: "var(--radius-md)",
            background: signLink.emailed ? "var(--color-success-bg-subtle)" : "var(--color-warning-bg-subtle)",
            border: `1px solid ${signLink.emailed ? "var(--color-success-border)" : "var(--color-warning-border)"}`,
          }}>
            <div style={{ fontSize: 12, color: signLink.emailed ? "var(--color-success-fg)" : "var(--color-warning-fg)", marginBottom: 8 }}>
              {signLink.emailed
                ? `Emailed to ${signLink.sentTo}. You can also share the link below.`
                : `Email not sent — ${signLink.emailError ?? "unknown reason"}. Share this link with the employee:`}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                readOnly
                value={signLink.url}
                onFocus={(e) => e.currentTarget.select()}
                style={{
                  flex: "1 1 220px", minWidth: 0, padding: "6px 9px", fontSize: 12,
                  border: "1px solid var(--color-surface-300)", borderRadius: "var(--radius-md)",
                  background: "var(--color-surface-0)", fontFamily: "monospace",
                }}
              />
              <button onClick={() => void copySignLink()} style={onbBtnSecondary}>
                {linkCopied ? "Copied ✓" : "Copy link"}
              </button>
              <button onClick={openSignPopup} style={onbBtnSecondary}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><ExternalLink size={12} /> Open Signing Page</span>
              </button>
            </div>
            <div style={{ fontSize: 11, color: "var(--color-surface-500)", marginTop: 6 }}>
              Link expires {new Date(signLink.expiresAt).toLocaleDateString("en-IN")} · single use · only the employee can sign
            </div>
          </div>
        )}

        {/* IT / AD Account — created once the employee has submitted (their details are final by then). */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-surface-100 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-[1_1_180px] text-[13px] font-medium">IT Provisioning / AD</span>
            {adStatus?.adAccountStatus === "ACTIVE" && <Badge tone="success">Active</Badge>}
            {adStatus?.adAccountStatus === "FAILED" && <Badge tone="error">Failed</Badge>}
            {!adLoading && !adStatus?.adAccountStatus && <span className="text-xs text-surface-400">Not provisioned</span>}
            {adLoading && <span className="text-xs text-surface-400">Checking…</span>}
          </div>
          {isSrHR && !adLoading && (!adStatus?.adAccountStatus || adStatus.adAccountStatus === "FAILED") && (
            <WhyDisabled reason={reviewReason}>
              <Button size="sm" variant={adStatus?.adAccountStatus === "FAILED" ? "destructive" : "default"} disabled={!!reviewReason} onClick={() => setAdConfirmOpen(true)}>
                {adStatus?.adAccountStatus === "FAILED" ? "Retry" : "Create AD Account"}
              </Button>
            </WhyDisabled>
          )}
        </div>
      </div>

      {adConfirmOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setAdConfirmOpen(false)}>
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", width: "min(420px, 90vw)", padding: 24 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Create AD Account?</h3>
            <p style={{ fontSize: 13, color: "var(--color-surface-600)", marginBottom: 16 }}>This checks for an existing account first. If none exists, it provisions a new one and generates a temporary password for HR to share securely — no email is sent automatically.</p>
            <div style={{ display: "flex", gap: 8 }}>
              <AsyncButton onClick={() => createAdAccount()} disabled={adCreating}>Confirm</AsyncButton>
              <button onClick={() => setAdConfirmOpen(false)} style={onbBtnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {adExistsPrompt && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setAdExistsPrompt(null)}>
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", width: "min(440px, 90vw)", padding: 24 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Account Already Exists</h3>
            <p style={{ fontSize: 13, color: "var(--color-surface-600)", marginBottom: 16 }}>
              An Active Directory account already exists for <strong>{adExistsPrompt.upn}</strong>. Since this employee is being migrated from an existing system, no new account will be created and no email will be sent — confirm to mark AD provisioning as complete.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <AsyncButton onClick={() => createAdAccount(true)} disabled={adCreating}>Confirm — Mark as Provisioned</AsyncButton>
              <button onClick={() => setAdExistsPrompt(null)} style={onbBtnSecondary}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* The temporary password is never emailed — HR hands it over through a secure channel. */}
      {adResult && (
        <div className="mt-2 rounded-md border border-[var(--color-success-border)] bg-[var(--color-success-bg-subtle)] p-3">
          <p className="mb-2 text-xs text-[var(--color-success-fg)]">
            AD account created. Temporary password — share it with the employee securely; it is not emailed.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Input readOnly value={adResult.tempPassword} className="h-8 w-56 font-mono text-xs" onFocus={(e) => e.currentTarget.select()} />
            <Button size="sm" variant="secondary" onClick={() => void copyPassword()}>
              <Copy size={12} /> {passwordCopied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      )}

      {/* Confirm Ready for Payroll / Activate — only once submitted and every gate passes. */}
      {isSrHR && !completed && (
        <div className="mt-3 border-t border-surface-100 pt-3.5">
          <WhyDisabled reason={activateReason}>
            <AsyncButton
              onClick={() => confirmReadyForPayroll()}
              confirm={{
                title: "Confirm ready for payroll?",
                description: "Runs the payroll readiness checks and, if every one of them passes, activates this employee.",
                actionText: "Confirm Ready",
                tone: "success",
              }}
              disabled={payrollChecking || activating || !!activateReason}
            >
              Confirm Ready for Payroll
            </AsyncButton>
          </WhyDisabled>
          {submitted && activationBlockers.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-surface-500">
              {activationBlockers.map(b => <li key={b}>{b}</li>)}
            </ul>
          )}
          {payrollResult && !payrollResult.ready && (
            <div className="mt-2.5 text-xs text-[var(--color-error-fg)]">
              <div className="mb-1 font-semibold">{payrollResult.message}</div>
              <ul className="list-disc pl-5">
                {payrollResult.missing.map(m => <li key={m}>{m}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      </fieldset>
    </CollapsibleSection>
  );
}
